import React, { useEffect, useState } from "react";

import { lessonRequestService } from "api/services/lesson-request.service";
import ContentHeader from "components/Layout/ContentHeader";
import LessonRequestDetailModal from "components/schedule-group/LessonRequestDetailModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import { getApiErrorMessage } from "utils/api-error";
import { LESSON_REQUEST_STATUS_BADGE, LESSON_REQUEST_STATUS_OPTIONS, PERIOD_PRESETS, type PeriodPreset } from "utils/constant/lesson-request";

import type { LessonRequestListItem, LessonRequestStatus } from "api/types/lesson-request.types";

import "pages/AdminPage.scss";
import "./LessonRequestList.scss";

const PAGE_SIZE = 10;

const pad = (n: number) => String(n).padStart(2, "0");
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// 프리셋 → date_from (신청일 created_at 기준, 오늘까지)
const presetToDateFrom = (preset: PeriodPreset): string => {
  const from = new Date();
  if (preset === "1w") from.setDate(from.getDate() - 7);
  else if (preset === "1m") from.setMonth(from.getMonth() - 1);
  else if (preset === "3m") from.setMonth(from.getMonth() - 3);
  return toDateStr(from);
};

const formatDateTime = (v: string): string => (v ? v.slice(0, 10) : "-");

const LessonRequestList: React.FC = () => {
  const [items, setItems] = useState<LessonRequestListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 필터 상태
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [period, setPeriod] = useState<PeriodPreset>("3m");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // 상세 모달
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const resolveDateRange = (): { date_from?: string; date_to?: string } => {
    if (period === "custom") {
      return {
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      };
    }
    return { date_from: presetToDateFrom(period), date_to: toDateStr(new Date()) };
  };

  const fetchList = async (page: number) => {
    setLoading(true);
    setError("");
    try {
      const res = await lessonRequestService.list({
        page,
        page_size: PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter as LessonRequestStatus } : {}),
        ...resolveDateRange(),
      });
      setItems(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err) {
      setError(getApiErrorMessage(err));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, period]);

  const handleSearch = () => {
    if (currentPage !== 1) setCurrentPage(1);
    else fetchList(1);
  };

  const openDetail = (row: LessonRequestListItem) => {
    setSelectedId(row.id);
    setModalOpen(true);
  };

  const columns: Column<LessonRequestListItem>[] = [
    { key: "lesson_name", title: "강습명", render: (v) => v || "-" },
    { key: "request_type_display", title: "수업종류", width: "150px" },
    { key: "applicant_name", title: "신청자" },
    { key: "applicant_user_id", title: "아이디" },
    {
      key: "status",
      title: "수락여부",
      width: "100px",
      render: (v, row) => <Badge variant={LESSON_REQUEST_STATUS_BADGE[v as LessonRequestStatus] ?? "default"}>{row.status_display}</Badge>,
    },
    {
      key: "accepted_instructor_name",
      title: "담당강사",
      render: (v, row) =>
        v ? (
          <span>
            {v}
            <span className="lesson-request__muted"> (후보 {row.candidate_count}명)</span>
          </span>
        ) : (
          <span className="lesson-request__muted">- (후보 {row.candidate_count}명)</span>
        ),
    },
    { key: "requested_date", title: "희망날짜", width: "110px" },
    { key: "location_name", title: "장소" },
    {
      key: "created_at",
      title: "요청날짜",
      width: "110px",
      render: (v) => formatDateTime(v),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="강습 요청" description="회원의 콜잡기(강습 개설 요청) 신청 이력과 처리 결과를 조회합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter lesson-request__filter">
          <Input placeholder="회원 실명 또는 아이디 검색" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ width: 240 }} prefix="🔍" />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={LESSON_REQUEST_STATUS_OPTIONS}
            style={{ width: 140 }}
          />

          <div className="lesson-request__periods">
            {PERIOD_PRESETS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "primary" : "outline"}
                onClick={() => {
                  setPeriod(p.value);
                  setCurrentPage(1);
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>

          {period === "custom" && (
            <div className="lesson-request__date-range">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="lesson-request__date-input" />
              <span>~</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="lesson-request__date-input" />
            </div>
          )}

          <Button variant="secondary" size="sm" onClick={handleSearch}>
            조회
          </Button>
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={items} rowKey="id" onRowClick={openDetail} />}

        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={setCurrentPage} />
      </div>

      <LessonRequestDetailModal open={modalOpen} requestId={selectedId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default LessonRequestList;
